/**
 * Tournament Event participation age bands — presets + custom min/max rules.
 * Validation uses player DOB at competition reference date (start, else now).
 */

const AGE_OPEN = "open";
const AGE_CUSTOM = "custom";
const AGE_U15 = "u15";
const AGE_U17 = "u17";
const AGE_U19 = "u19";
const AGE_ADULT = "adult";
const AGE_35 = "35+";
const AGE_40 = "40+";
const AGE_45 = "45+";
const AGE_50 = "50+";

/** Preset id → rule (null open = no check). maxAge/minAge inclusive in whole years. */
const PRESET_AGE_RULES = Object.freeze({
  [AGE_OPEN]: null,
  [AGE_U15]: Object.freeze({ label: "U15", maxAge: 15 }),
  [AGE_U17]: Object.freeze({ label: "U17", maxAge: 17 }),
  [AGE_U19]: Object.freeze({ label: "U19", maxAge: 19 }),
  [AGE_ADULT]: Object.freeze({ label: "Adult", minAge: 19, maxAge: 34 }),
  [AGE_35]: Object.freeze({ label: "35+", minAge: 35 }),
  [AGE_40]: Object.freeze({ label: "40+", minAge: 40 }),
  [AGE_45]: Object.freeze({ label: "45+", minAge: 45 }),
  [AGE_50]: Object.freeze({ label: "50+", minAge: 50 }),
});

/** @typedef {{ label?: string, minAge?: number | null, maxAge?: number | null }} AgeBandRule */

/**
 * @param {string | number | null | undefined} dateOfBirthMs
 * @param {number} [asOfMs]
 * @returns {number | null}
 */
export function ageInWholeYears(dateOfBirthMs, asOfMs = Date.now()) {
  if (dateOfBirthMs == null || dateOfBirthMs === "") return null;
  const ms = typeof dateOfBirthMs === "number" ? dateOfBirthMs : Date.parse(String(dateOfBirthMs));
  if (!Number.isFinite(ms)) return null;
  const birth = new Date(ms);
  if (Number.isNaN(birth.getTime())) return null;
  const ref = new Date(asOfMs);
  let years = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
    years -= 1;
  }
  return years >= 0 && years <= 120 ? years : null;
}

/**
 * @param {AgeBandRule | null | undefined} rule
 * @returns {boolean}
 */
export function isOpenAgeBand(rule) {
  return rule == null;
}

/**
 * @param {object | null | undefined} participation
 * @returns {AgeBandRule | null} null = open / no age gate
 */
export function resolveParticipationAgeBand(participation) {
  if (!participation || typeof participation !== "object") return null;
  const ageId = String(participation.age ?? AGE_OPEN).trim() || AGE_OPEN;
  if (ageId === AGE_OPEN) return null;

  if (ageId === AGE_CUSTOM || participation.ageRule) {
    const raw = participation.ageRule;
    if (!raw || typeof raw !== "object") return null;
    const minAge = raw.minAge != null && raw.minAge !== "" ? Number(raw.minAge) : null;
    const maxAge = raw.maxAge != null && raw.maxAge !== "" ? Number(raw.maxAge) : null;
    const label = String(raw.label || "").trim() || undefined;
    const hasMin = Number.isFinite(minAge);
    const hasMax = Number.isFinite(maxAge);
    if (!hasMin && !hasMax) return null;
    return {
      label: label || formatAgeBandLabel({ minAge: hasMin ? minAge : null, maxAge: hasMax ? maxAge : null }),
      minAge: hasMin ? minAge : null,
      maxAge: hasMax ? maxAge : null,
    };
  }

  const preset = PRESET_AGE_RULES[ageId];
  if (preset === undefined) {
    const parsed = parseAgeBandToken(ageId);
    return parsed;
  }
  return preset;
}

/**
 * Legacy / shorthand tokens (e.g. stored label "80+").
 * @param {string} token
 * @returns {AgeBandRule | null}
 */
export function parseAgeBandToken(token) {
  const t = String(token || "").trim();
  if (!t || t === AGE_OPEN) return null;
  const uMatch = /^u(\d{1,2})$/i.exec(t);
  if (uMatch) {
    const max = Number(uMatch[1]);
    if (Number.isFinite(max)) return { label: `U${max}`, maxAge: max };
  }
  const plusMatch = /^(\d{1,3})\+$/.exec(t);
  if (plusMatch) {
    const min = Number(plusMatch[1]);
    if (Number.isFinite(min)) return { label: `${min}+`, minAge: min };
  }
  return null;
}

/**
 * @param {AgeBandRule | null | undefined} rule
 */
export function formatAgeBandLabel(rule) {
  if (!rule) return "Open";
  if (rule.label) return rule.label;
  if (rule.minAge != null && rule.maxAge != null) {
    return `${rule.minAge}–${rule.maxAge}`;
  }
  if (rule.minAge != null) return `${rule.minAge}+`;
  if (rule.maxAge != null) return `U${rule.maxAge}`;
  return "Age restricted";
}

/**
 * @param {number | null} playerAge
 * @param {AgeBandRule | null | undefined} rule
 * @returns {{ ok: boolean, reason?: string }}
 */
export function playerAgeMeetsBand(playerAge, rule) {
  if (isOpenAgeBand(rule)) return { ok: true };
  if (playerAge == null) {
    return { ok: false, reason: "Date of birth is required for this age category." };
  }
  if (rule.minAge != null && playerAge < rule.minAge) {
    return {
      ok: false,
      reason: `This event is ${formatAgeBandLabel(rule)}. You must be at least ${rule.minAge} years old.`,
    };
  }
  if (rule.maxAge != null && playerAge > rule.maxAge) {
    return {
      ok: false,
      reason: `This event is ${formatAgeBandLabel(rule)}. Maximum age is ${rule.maxAge}.`,
    };
  }
  return { ok: true };
}

/**
 * Reference date for age checks (tournament start preferred).
 * @param {object | null | undefined} competition
 */
export function ageReferenceMs(competition) {
  const start = competition?.dates?.startMs;
  if (start != null && Number.isFinite(Number(start))) return Number(start);
  return Date.now();
}

/**
 * @param {import('./entryManagementTypes').EntryPlayerSnapshot} member
 * @param {AgeBandRule | null} rule
 * @param {number} asOfMs
 * @param {{ requireProfileDob?: boolean }} [options]
 */
export function validateMemberAgeBand(member, rule, asOfMs, options = {}) {
  if (isOpenAgeBand(rule)) return { ok: true };

  const hasAccount = Boolean(member?.playerId || member?.linkedUserId);
  const dobMs = member?.dateOfBirthMs ?? null;
  const playerAge = ageInWholeYears(dobMs, asOfMs);

  if (hasAccount || options.requireProfileDob) {
    if (playerAge == null) {
      return {
        ok: false,
        field: "members",
        message:
          "Add your date of birth on your player profile to register for this age category.",
      };
    }
  } else if (playerAge == null) {
    return {
      ok: false,
      field: "members",
      message: "Date of birth is required for age-restricted events.",
    };
  }

  const check = playerAgeMeetsBand(playerAge, rule);
  if (!check.ok) {
    return {
      ok: false,
      field: "members",
      message: check.reason || "Player does not meet the age requirement for this event.",
    };
  }
  return { ok: true };
}

/**
 * @param {object | null | undefined} ageRule
 * @returns {{ ok: boolean, error?: string, rule?: AgeBandRule | null }}
 */
export function normalizeCustomAgeRule(ageRule) {
  if (!ageRule || typeof ageRule !== "object") {
    return { ok: false, error: "Enter a minimum and/or maximum age for a custom band." };
  }
  const minAge = ageRule.minAge != null && ageRule.minAge !== "" ? Number(ageRule.minAge) : null;
  const maxAge = ageRule.maxAge != null && ageRule.maxAge !== "" ? Number(ageRule.maxAge) : null;
  const hasMin = Number.isFinite(minAge);
  const hasMax = Number.isFinite(maxAge);
  if (!hasMin && !hasMax) {
    return { ok: false, error: "Enter a minimum and/or maximum age for a custom band." };
  }
  if (hasMin && (minAge < 0 || minAge > 120)) {
    return { ok: false, error: "Minimum age must be between 0 and 120." };
  }
  if (hasMax && (maxAge < 0 || maxAge > 120)) {
    return { ok: false, error: "Maximum age must be between 0 and 120." };
  }
  if (hasMin && hasMax && minAge > maxAge) {
    return { ok: false, error: "Minimum age cannot be greater than maximum age." };
  }
  const label = String(ageRule.label || "").trim() || undefined;
  return {
    ok: true,
    rule: {
      label,
      minAge: hasMin ? minAge : null,
      maxAge: hasMax ? maxAge : null,
    },
  };
}

export { PRESET_AGE_RULES };
